import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';

import type { RootState } from '@app/store/store';
import { tokenManagementApiSlice } from '@features/token-management/api/token-management.api';

// A universal dispatch type. It allows this file to be used both by React components
// and by Redux background tasks (like `invokeLogout`).
type AppThunkDispatch = ThunkDispatch<RootState, unknown, UnknownAction>;

/**
 * THE RESERVATION REGISTRY (Singleton)
 * * Problem: The backend does not automatically garbage-collect abandoned reservations.
 * If a user opens the "New Investigation" modal, a token reservation is created.
 * If they are suddenly logged out (e.g., cross-tab inactivity), we MUST cancel that
 * reservation BEFORE wiping the user's access token from memory. If we rely on the
 * React Modal unmounting to do this, it will be too late: the token is gone,
 * the cancel request fails (401), and the reservation leaks forever on the backend.
 * * Solution: This global variable lives completely outside of React.
 * Our Logout functions can look at this variable, say "Aha, a reservation is pending!",
 * and cancel it safely while the access token is still completely valid.
 */
let trackedReservationId: string | null = null;

/**
 * Remembers the ID of an active reservation.
 * Call with `null` when the reservation is successfully consumed by a Launch,
 * so we forget about it and don't accidentally cancel it.
 */
export const trackReservation = (reservationId: string | null): void => {
  trackedReservationId = reservationId;
};

/**
 * The actual Cleanup Function.
 * * It is `async` so the Logout process can `await` it, guaranteeing the network
 * request finishes BEFORE the access token is deleted.
 * * It is also idempotent: By setting `trackedReservationId = null` immediately,
 * we guarantee the API call only runs once, even if both React Unmount and Redux Logout
 * try to call it at the exact same millisecond.
 */
export const cancelTrackedReservation = async (dispatch: AppThunkDispatch): Promise<void> => {
  const reservationId = trackedReservationId;

  // Nothing to cancel (either no reservation, or another process already handled it)
  if (!reservationId) {
    return;
  }

  // Clear it immediately to prevent duplicate API calls
  trackedReservationId = null;

  try {
    await dispatch(tokenManagementApiSlice.endpoints.cancelTokenReservation.initiate(reservationId)).unwrap();
  } catch (error) {
    // If it fails, we swallow the error because the user is logging out anyway.
    console.error('Failed to cancel token reservation during teardown', error);
  }
};
